import { useState } from 'react'
import { formatDateTime } from '@/lib/dates'
import { ExternalLink, Scroll } from 'lucide-react'
import type { ActionTrailEvent, Ticket, TicketDetail, ViewingAs } from '@/types/ticket'
import { ActionTrail } from './ActionTrail'
import { useIsAdmin } from '@/lib/auth'
import { TicketEmails } from '@/features/email-log/TicketEmails'

const CONTEXT_LABELS: Record<string, string> = {
  pageUrl: 'Page',
  userAgent: 'Browser',
  appVersion: 'App version',
  betterStackTraceId: 'BetterStack ID',
  source: 'Source',
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[82px_1fr] items-baseline gap-x-4 gap-y-1 text-etn-control">
      <span className="text-etn-text-soft">{label}</span>
      <span className="min-w-0 break-words text-etn-text-cell">{children}</span>
    </div>
  )
}

function ContextValue({ value }: { value: unknown }) {
  if (value == null) return <span className="text-etn-text-soft">—</span>
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-etn-micro italic text-etn-text-soft">empty</span>
    return <span className="font-mono text-etn-micro">{JSON.stringify(value)}</span>
  }
  if (typeof value === 'object') {
    return <span className="font-mono text-etn-micro">{JSON.stringify(value)}</span>
  }
  return <span className="break-all">{String(value)}</span>
}

// Everything in this card is reference material the reader only wants when
// they go looking for it, so each group collapses behind a micro-label
// disclosure and the whole card sits on the sunken grey rather than competing
// with the conversation for attention (approved mockup, "Conversation + slim
// rail").
function Disclosure({
  label,
  children,
  testId,
  onToggle,
}: {
  label: string
  children: React.ReactNode
  testId?: string
  onToggle?: (open: boolean) => void
}) {
  return (
    <details
      className="border-b border-etn-border-hairline last:border-b-0"
      data-testid={testId}
      onToggle={onToggle ? e => onToggle((e.currentTarget as HTMLDetailsElement).open) : undefined}
    >
      <summary className="flex list-none items-center py-2.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label after:ml-auto after:text-etn-text-soft after:content-['⌄'] [&::-webkit-details-marker]:hidden">
        {label}
      </summary>
      <div className="pb-3">{children}</div>
    </details>
  )
}

export function ContextPanel({ ticket, links, viewingAs }: { ticket: Ticket; links?: TicketDetail['links']; viewingAs?: ViewingAs }) {
  const isAdmin = useIsAdmin()
  const [emailsOpened, setEmailsOpened] = useState(false)
  const ctx = (ticket.context ?? {}) as Record<string, unknown>
  const actionTrail: ActionTrailEvent[] = Array.isArray(ctx.actionTrail)
    ? (ctx.actionTrail as ActionTrailEvent[])
    : []
  // Render every other context field in the generic panel; the trail has its own UI.
  const ctxEntries = Object.entries(ctx).filter(
    ([k, v]) => k !== 'actionTrail' && v !== undefined && v !== null && v !== '',
  )

  return (
    <div className="rounded-etn-card bg-etn-surface-sunken px-3.5 py-0.5" data-testid="ticket-metacard">
      <Disclosure label="Details" testId="details-disclosure">
        <div className="space-y-1">
          {ticket.creator && <Row label="Reporter">{ticket.creator.name ?? ticket.creator.email ?? '—'}</Row>}
          <Row label="Created">{formatDateTime(ticket.createdAt)}</Row>
          {ticket.closedAt && <Row label="Closed">{formatDateTime(ticket.closedAt)}</Row>}
        </div>
      </Disclosure>

      {ctxEntries.length > 0 && (
        <Disclosure label="Page context" testId="page-context-disclosure">
          <div className="space-y-1">
            {ctxEntries.map(([k, v]) => (
              <Row key={k} label={CONTEXT_LABELS[k] ?? k}>
                <ContextValue value={v} />
              </Row>
            ))}
          </div>
        </Disclosure>
      )}

      {links?.betterStackLogs && (
        <Disclosure label="Tools">
          <a
            href={links.betterStackLogs}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-etn-menu border border-etn-border-hairline bg-etn-surface px-2.5 py-1.5 text-etn-micro font-semibold text-etn-text-label transition-colors hover:text-etn-text-cell"
          >
            <Scroll className="h-3.5 w-3.5 text-etn-text-soft" />
            Open server logs
            <ExternalLink className="h-3 w-3 text-etn-text-soft" />
          </a>
        </Disclosure>
      )}

      {!viewingAs && isAdmin && (
        <Disclosure label="Emails" testId="emails-disclosure" onToggle={open => open && setEmailsOpened(true)}>
          <TicketEmails ticketId={ticket._id} enabled={emailsOpened} />
        </Disclosure>
      )}

      {/* Staff-only trail; the API strips it under the view-as lens, so skip rendering it there too.
          The isAdmin check duplicates ActionTrail's own so a non-admin doesn't get an empty disclosure. */}
      {!viewingAs && isAdmin && (
        <Disclosure label="Action trail" testId="action-trail-disclosure">
          <ActionTrail ticketId={ticket._id} ticketCreatedAt={ticket.createdAt} snapshot={actionTrail} embedded />
        </Disclosure>
      )}
    </div>
  )
}
